import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { shouldUsePinMode } from '@/utils/pinModeUtils';

export interface SavedView {
  id: string;
  user_id: string;
  name: string;
  filters: ReportFilters;
  grouping: string;
  is_shared: boolean;
  created_at: string;
}

export interface ReportFilters {
  employees: string[];
  projects: string[];
  status: 'all' | 'active' | 'closed';
  billable: 'all' | 'yes' | 'no';
  invoiced: 'all' | 'yes' | 'no';
}

export const DEFAULT_FILTERS: ReportFilters = {
  employees: [],
  projects: [],
  status: 'all',
  billable: 'all',
  invoiced: 'all',
};

export const useSavedViews = () => {
  const [views, setViews] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState(false);

  const getPin = () => localStorage.getItem('manager_pin') || localStorage.getItem('employee_pin') || '';

  const fetchViews = useCallback(async () => {
    if (!shouldUsePinMode()) return;
    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('pin_manage_saved_views', {
        p_pin: getPin(),
        p_action: 'list',
      });
      if (error) throw error;
      setViews(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erreur chargement vues:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchViews();
  }, [fetchViews]);

  const createView = async (name: string, filters: ReportFilters, grouping: string, isShared: boolean) => {
    const { data, error } = await supabase.rpc('pin_manage_saved_views', {
      p_pin: getPin(),
      p_action: 'create',
      p_name: name,
      p_filters: filters as any,
      p_grouping: grouping,
      p_is_shared: isShared,
    });
    if (error) throw error;
    await fetchViews();
    return data;
  };

  const deleteView = async (viewId: string) => {
    const { error } = await supabase.rpc('pin_manage_saved_views', {
      p_pin: getPin(),
      p_action: 'delete',
      p_view_id: viewId,
    });
    if (error) throw error;
    await fetchViews();
  };

  return { views, loading, createView, deleteView, refetch: fetchViews };
};
